import React, { useMemo } from 'react';
import SectionCard from '../components/SectionCard';
import { Ban } from 'lucide-react';

const fmtNum = (n, d = 0) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: d });
const fmtKg = (n) => `${fmtNum(n, 1)} kg`;

// Sección: Ciclos abortados — detalle de causas y kilos perdidos.
// `abortados` es el listado crudo (abortado: true) del periodo.
const AbortadosSection = ({ abortados }) => {
  const { data, loading, error, reload } = abortados;

  const computed = useMemo(() => {
    const docs = Array.isArray(data) ? data : [];
    const ciclos = docs.reduce((s, c) => s + Number(c.ciclos || 0), 0);
    const kilos = docs.reduce((s, c) => s + Number(c.ciclos || 0) * Number(c.capacidad || 0), 0);

    // Causas de abortado: agrupamos por `razonAbortado`, sumando la cantidad
    // de ciclos (no de documentos) que cada causa representa.
    const causasMap = new Map();
    docs.forEach((c) => {
      const causa = c.razonAbortado?.trim() || 'Sin clasificar';
      const n = Number(c.ciclos || 0);
      causasMap.set(causa, (causasMap.get(causa) || 0) + n);
    });
    const causas = Array.from(causasMap.entries())
      .map(([causa, valor]) => ({ causa, valor }))
      .sort((a, b) => b.valor - a.valor);
    const maxCausa = causas.length ? causas[0].valor : 0;

    return { ciclos, kilos, causas, maxCausa };
  }, [data]);

  return (
    <SectionCard
      title="Ciclos abortados"
      hint="Carga iniciada que no terminó el programa."
      loading={loading}
      error={error}
      onReload={reload}
    >
      {!loading && computed.ciclos === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          <Ban className="h-8 w-8 mb-2 text-gray-300 dark:text-gray-600" />
          Sin ciclos abortados en el periodo seleccionado.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-[#FEECEF] dark:bg-red-900/20 rounded-[10px] p-4 border-l-4 border-[#FC536D]">
              <div className="text-[11px] font-semibold tracking-wider uppercase text-[#B71D3F] opacity-75 mb-1.5">
                Ciclos
              </div>
              <div className="font-['Manrope'] text-[28px] font-extrabold text-[#B71D3F] leading-none">
                {fmtNum(computed.ciclos)}
              </div>
            </div>
            <div className="bg-[#FEECEF] dark:bg-red-900/20 rounded-[10px] p-4 border-l-4 border-[#FC536D]">
              <div className="text-[11px] font-semibold tracking-wider uppercase text-[#B71D3F] opacity-75 mb-1.5">
                Kilos (nominal)
              </div>
              <div className="font-['Manrope'] text-[28px] font-extrabold text-[#B71D3F] leading-none">
                {fmtNum(computed.kilos, 1)}
              </div>
            </div>
          </div>

          <div className="text-[11px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 mb-2.5">
            Causas de abortado
          </div>
          <div className="space-y-2">
            {computed.causas.map(({ causa, valor }) => (
              <div key={causa} className="flex items-center gap-2.5">
                <span className="text-xs flex-1 text-[#1A1C1C] dark:text-gray-200">{causa}</span>
                <span className="w-[120px] h-[7px] bg-[#EEEEEE] dark:bg-gray-700 rounded-full overflow-hidden">
                  <span
                    className="block h-full bg-[#FC536D] rounded-full"
                    style={{ width: computed.maxCausa > 0 ? `${(valor / computed.maxCausa) * 100}%` : '0%' }}
                  />
                </span>
                <span className="font-['Manrope'] text-xs font-bold w-[40px] text-right tabular-nums text-[#1A1C1C] dark:text-gray-200">
                  {fmtNum(valor)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
};

export default AbortadosSection;
